import type { ReactElement } from 'react';
import { BatchStageBadge } from '@/components/common/BatchStageBadge';
import {
  createActionsColumn,
  createSerialColumn,
  DrillName,
  StackedCellText,
} from '@/components/common/DataTable';
import { HighlightText } from '@/components/common/HighlightText';
import { RowActionButton, RowActionGroup, RowViewRender } from '@/components/common/RowActions';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatDisplayDate } from '@/utils/date';
import { OPTION_DETAIL_SEPARATOR } from '@/utils/filters';
import { BatchFooterAmount } from './BatchFooterAmount';
import { BillButton } from './BillButton';
import type { Batch, BatchMember } from '../types/publish.types';
import { formatBatchAmount } from '../utils/formatBatchAmount';
import type { ColumnDef } from '@tanstack/react-table';

export interface BatchTotals {
  dcrAmount: number;
  derAmount: number;
  totalAmount: number;
  specimenCount: number;
}

type MemberAmountKey = 'dcrAmount' | 'derAmount' | 'totalAmount' | 'specimenCount';

function memberTotal(rows: readonly BatchMember[], key: MemberAmountKey): number {
  return rows.reduce((sum, member) => sum + member[key], 0);
}

export interface BatchRowAction {
  label: string;
  onClick: (batch: Batch) => void;
  when: (batch: Batch) => boolean;
  tone?: 'default' | 'destructive';
}

export function getBatchColumns(
  getMembersLink: (batch: Batch) => ReactElement,
  searchText: string | undefined,
  actions?: readonly BatchRowAction[],
  totals?: BatchTotals,
): ColumnDef<Batch, unknown>[] {
  return [
    { ...createSerialColumn<Batch>(), footer: 'Grand Total' },
    {
      id: 'batch',
      header: 'Batch',
      cell: ({ row }) => {
        const batchNumber = `#${String(row.original.legacyId)}`;
        return (
          <DrillName
            value={batchNumber}
            label={`Members of batch ${String(row.original.legacyId)}`}
            render={getMembersLink(row.original)}
            highlightedValue={<HighlightText text={batchNumber} highlightedText={searchText} />}
          />
        );
      },
    },
    {
      id: 'window',
      header: 'Covering',
      cell: ({ row }) =>
        row.original.trnDateFrom === null
          ? '—'
          : `${formatDisplayDate(row.original.trnDateFrom)} – ${formatDisplayDate(row.original.trnDateTo)}`,
    },
    {
      id: 'published',
      header: 'Published',
      cell: ({ row }) => (
        <StackedCellText
          primary={formatDisplayDate(row.original.publishDate)}
          secondary={
            row.original.publishedBy === null ? null : (
              <>
                <HighlightText text={row.original.publishedBy} highlightedText={searchText} />
                {row.original.publishedByMobile === null
                  ? null
                  : `${OPTION_DETAIL_SEPARATOR}${row.original.publishedByMobile}`}
              </>
            )
          }
        />
      ),
    },
    {
      id: 'dcr',
      header: 'DCR',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatBatchAmount(row.original.dcrAmount)}</span>
      ),
      footer: () => <BatchFooterAmount amount={totals?.dcrAmount} />,
    },
    {
      id: 'der',
      header: 'DER',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatBatchAmount(row.original.derAmount)}</span>
      ),
      footer: () => <BatchFooterAmount amount={totals?.derAmount} />,
    },
    {
      id: 'total',
      header: 'Total',
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">
          {formatBatchAmount(row.original.totalAmount)}
        </span>
      ),
      footer: () => <BatchFooterAmount amount={totals?.totalAmount} />,
    },
    {
      id: 'specimens',
      header: 'SD Count',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatBatchAmount(row.original.specimenCount)}</span>
      ),
      footer: () => <BatchFooterAmount amount={totals?.specimenCount} />,
    },
    {
      id: 'members',
      header: 'ZMs',
      cell: ({ row }) =>
        row.original.rowsArchived ? (
          <span className="text-xs text-muted-foreground">archived</span>
        ) : (
          <span className="tabular-nums">{formatBatchAmount(row.original.memberCount)}</span>
        ),
    },
    {
      id: 'disbursed',
      header: 'Disbursed',
      cell: ({ row }) =>
        row.original.disburseDate === null
          ? EMPTY_VALUE
          : formatDisplayDate(row.original.disburseDate),
    },
    {
      id: 'stage',
      header: 'Stage',
      cell: ({ row }) => <BatchStageBadge stage={row.original.stage} />,
    },
    createActionsColumn<Batch>((batch) => (
      <RowActionGroup>
        {(actions ?? [])
          .filter((rowAction) => rowAction.when(batch))
          .map((rowAction) => (
            <RowActionButton
              key={rowAction.label}
              label={rowAction.label}
              ariaLabel={`${rowAction.label} batch ${batch.legacyId}`}
              tone={rowAction.tone ?? 'default'}
              onClick={() => rowAction.onClick(batch)}
            />
          ))}
        <RowViewRender
          render={getMembersLink(batch)}
          label="Members"
          ariaLabel={`Members of batch ${String(batch.legacyId)}`}
        />
      </RowActionGroup>
    )),
  ];
}

export function getBatchMemberColumns(
  onBill?: (member: BatchMember) => Promise<void>,
  rows: readonly BatchMember[] = [],
): ColumnDef<BatchMember, unknown>[] {
  const columns: ColumnDef<BatchMember, unknown>[] = [
    { ...createSerialColumn<BatchMember>(), footer: 'Total' },
    {
      id: 'member',
      header: 'ZM Name',
      cell: ({ row }) => (
        <StackedCellText
          primary={row.original.fullName ?? EMPTY_VALUE}
          primaryClassName="font-medium"
          secondary={[row.original.hrEmpId, row.original.designationName]
            .filter(Boolean)
            .join(OPTION_DETAIL_SEPARATOR)}
        />
      ),
    },
    {
      id: 'mobile',
      header: 'Mobile',
      cell: ({ row }) => row.original.mobile ?? EMPTY_VALUE,
    },
    {
      id: 'department',
      header: 'Department',
      cell: ({ row }) => row.original.departmentName ?? EMPTY_VALUE,
    },
    {
      id: 'division',
      header: 'Division',
      cell: ({ row }) => row.original.divisionName ?? EMPTY_VALUE,
    },
    {
      id: 'where',
      header: 'Thana, District',
      cell: ({ row }) =>
        [row.original.thanaName, row.original.districtName].filter(Boolean).join(', ') ||
        EMPTY_VALUE,
    },
    {
      id: 'dcr',
      header: 'DCR',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatBatchAmount(row.original.dcrAmount)}</span>
      ),
      footer: () => <BatchFooterAmount amount={memberTotal(rows, 'dcrAmount')} />,
    },
    {
      id: 'der',
      header: 'DER',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatBatchAmount(row.original.derAmount)}</span>
      ),
      footer: () => <BatchFooterAmount amount={memberTotal(rows, 'derAmount')} />,
    },
    {
      id: 'total',
      header: 'Total',
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">
          {formatBatchAmount(row.original.totalAmount)}
        </span>
      ),
      footer: () => <BatchFooterAmount amount={memberTotal(rows, 'totalAmount')} />,
    },
    {
      id: 'specimens',
      header: 'SD Count',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatBatchAmount(row.original.specimenCount)}</span>
      ),
      footer: () => <BatchFooterAmount amount={memberTotal(rows, 'specimenCount')} />,
    },
  ];

  if (onBill === undefined) return columns;

  return [
    ...columns,
    {
      id: 'bill',
      header: 'Bill',
      cell: ({ row }) => (
        <BillButton onOpen={() => onBill(row.original)} memberName={row.original.fullName} />
      ),
    },
  ];
}
